import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { PAYMENT_REQUEST_TYPES } from "@/lib/workflow";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreatableCombobox } from "@/components/CreatableCombobox";
import { FileText, UploadCloud, Loader2, CheckCircle2, X, Eye, User, Layers, ReceiptText, Tags, Lock } from "lucide-react";

const NONE = "__none__";
const ALLOWED_EXT = ["pdf", "png", "jpg", "jpeg", "webp"];

// Verticals whose spend is always classified as Marketing (Cost Type locked).
const MARKETING_VERTICALS = ["ATL", "BTL", "CX", "Brand", "Digital"];
const isMarketingVertical = (v) => MARKETING_VERTICALS.includes(v);

const EMPTY = {
  spend_month: "", invoice_month: "",
  spend_type: "Project", corporate_entity: "",
  vertical: "", vis_perf: "", collateral_type: "",
  platform: "", vendor: "", billing_business: "",
  cost_type: "", activity: "", outcome: "",
  campaign_id: "", locked: false, payment_status: "Unpaid",
  invoice_file_id: "", invoice_file_name: "",
  // proforma / workflow metadata
  proforma_text: "", period: "", duration_months: [], payment_request: "Complete", remark: "",
  // one row per project (or corporate entity): { key, amount }
  allocations: [{ key: "", amount: "" }],
};

const SPEND_TYPES = ["Project", "Corporate"];
const PAYMENT_STATUSES = ["Paid", "Partial", "Unpaid"];

export function AddBillDrawer({ open, onOpenChange, lists, onSaved, editing, preset }) {
  const { mode } = useCurrency();
  const { user, isManager, isFull, isExecutive, assignedVerticals } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [tax, setTax] = useState(null);
  // New vendors / platforms / etc. typed inline (creatable dropdowns).
  const [extras, setExtras] = useState({ vendors: [], platforms: [], cost_types: [], activities: [], outcomes: [], vis_perf: [], collateral_types: [], billing_businesses: [] });

  // Finance Executives (+ Admin / Leader) may see & set payment status.
  const canSeePayment = isFull || isExecutive;
  // Managers with a single assigned vertical get a hard read-only lock.
  const managerLockVertical = isManager && (assignedVerticals || []).length === 1;
  const lockedVertical = managerLockVertical ? assignedVerticals[0] : "";
  const isEdit = !!(editing && editing.id);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSaveMsg("");
    setExtras({ vendors: [], platforms: [], cost_types: [], activities: [], outcomes: [], vis_perf: [], collateral_types: [], billing_businesses: [] });
    api.listCampaigns().then(setCampaigns).catch(() => setCampaigns([]));
    api.getSpendTaxonomy().then(setTax).catch(() => setTax(null));
    if (editing) {
      const key = editing.spend_type === "Corporate" ? (editing.corporate_entity || editing.project || "") : (editing.project || "");
      setForm({
        ...EMPTY, ...editing,
        allocations: [{ key, amount: editing.amount ?? "" }],
      });
    } else {
      const soloVertical = managerLockVertical
        ? lockedVertical
        : (isManager && (assignedVerticals || []).length === 1 ? assignedVerticals[0] : "");
      setForm({
        ...EMPTY,
        ...(soloVertical ? { vertical: soloVertical } : {}),
        allocations: [{ key: (preset && preset.project) || "", amount: "" }],
        ...(preset || {}),
      });
    }
  }, [open, editing, preset, isManager, assignedVerticals]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const clearErr = (k) => setErrors((e) => ({ ...e, [k]: undefined }));

  // ---- creatable-combobox option lists (existing ∪ inline-created ∪ current) ----
  const uniqOpts = (arr) => {
    const seen = new Set();
    const out = [];
    (arr || []).forEach((v) => {
      const val = (v || "").toString().trim();
      if (!val || seen.has(val)) return;
      seen.add(val);
      out.push({ value: val, label: val });
    });
    return out;
  };
  const vendorOptions = useMemo(
    () => uniqOpts([...(lists?.vendors || []), ...extras.vendors, ...(form.vendor ? [form.vendor] : [])]),
    [lists, extras.vendors, form.vendor]
  );
  const platformOptions = useMemo(
    () => uniqOpts([...(lists?.platforms || []), ...extras.platforms, ...(form.platform ? [form.platform] : [])]),
    [lists, extras.platforms, form.platform]
  );
  const costTypeOptions = useMemo(
    () => uniqOpts([...(lists?.cost_types || ["Marketing", "Operational", "Pre-Sales"]), ...extras.cost_types, ...(form.cost_type ? [form.cost_type] : [])]),
    [lists, extras.cost_types, form.cost_type]
  );
  const activityOptions = useMemo(
    () => uniqOpts([...(lists?.activities || []), ...extras.activities, ...(form.activity ? [form.activity] : [])]),
    [lists, extras.activities, form.activity]
  );
  const outcomeOptions = useMemo(
    () => uniqOpts([...(lists?.outcomes || []), ...extras.outcomes, ...(form.outcome ? [form.outcome] : [])]),
    [lists, extras.outcomes, form.outcome]
  );
  const visPerfOptions = useMemo(
    () => uniqOpts([...(lists?.vis_perf || []), ...extras.vis_perf, ...(form.vis_perf ? [form.vis_perf] : [])]),
    [lists, extras.vis_perf, form.vis_perf]
  );
  const collateralOptions = useMemo(
    () => uniqOpts([...(lists?.collateral_types || []), ...extras.collateral_types, ...(form.collateral_type ? [form.collateral_type] : [])]),
    [lists, extras.collateral_types, form.collateral_type]
  );
  const billingOptions = useMemo(
    () => uniqOpts([...(lists?.billing_businesses || []), ...extras.billing_businesses, ...(form.billing_business ? [form.billing_business] : [])]),
    [lists, extras.billing_businesses, form.billing_business]
  );

  const createValue = async (kind, raw) => {
    const val = (raw || "").trim();
    if (!val) return;
    setExtras((e) => ({ ...e, [kind]: Array.from(new Set([...(e[kind] || []), val])) }));
    try {
      if (kind === "vendors") {
        await api.updateLists({ vendors: Array.from(new Set([...(lists?.vendors || []), ...extras.vendors, val])) });
      } else if (kind === "platforms") {
        await api.updateLists({ platforms: Array.from(new Set([...(lists?.platforms || []), ...extras.platforms, val])) });
      } else if (["cost_types", "activities", "outcomes", "vis_perf", "collateral_types", "billing_businesses"].includes(kind)) {
        await api.updateLists({ [kind]: Array.from(new Set([...(lists?.[kind] || []), ...(extras[kind] || []), val])) });
      }
      toast.success(`Added "${val}"`);
    } catch (e) {
      toast.message(`Using "${val}" for this invoice`);
    }
  };

  // ---- PART 1 taxonomy: spend type -> (entity) -> scoped verticals ----
  const projVerticals = (tax && tax.project_verticals) || (lists?.verticals || []);
  const corpVerticals = (tax && tax.corporate_verticals) || [];
  const verticalLabels = (tax && tax.vertical_labels) || {};
  const entityOpts = (tax && tax.corporate_entities) || ["Sumadhura", "Signet", "Tech"];
  const isCorporate = form.spend_type === "Corporate";

  const verticalOpts = useMemo(() => {
    // Managers always classify into their assigned vertical(s) — regardless of
    // whether the spend is Project or Corporate.
    if (isManager) {
      return (assignedVerticals || []).map((v) => ({ value: v, label: verticalLabels[v] || v }));
    }
    const list = isCorporate ? corpVerticals : projVerticals;
    return (list || []).map((v) => ({ value: v, label: verticalLabels[v] || v }));
  }, [tax, isCorporate, isManager, assignedVerticals]); // eslint-disable-line react-hooks/exhaustive-deps

  const costLocked = isMarketingVertical(form.vertical);

  // ---- allocations (multi project / entity) ----
  const allocKeyLabel = (key) => {
    if (!key) return "";
    if (isCorporate) return key;
    const p = (lists?.projects || []).find((x) => x.name === key);
    return p ? (p.short || p.name) : key;
  };
  const selectedKeys = useMemo(() => new Set((form.allocations || []).map((a) => a.key).filter(Boolean)), [form.allocations]);
  const addableOptions = useMemo(() => {
    if (isCorporate) return (entityOpts || []).filter((e) => !selectedKeys.has(e));
    return (lists?.projects || []).filter((p) => !selectedKeys.has(p.name));
  }, [isCorporate, entityOpts, lists, selectedKeys]);

  const total = useMemo(
    () => (form.allocations || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0),
    [form.allocations]
  );

  const addAllocation = (key) => {
    if (!key) return;
    setForm((f) => {
      const rows = (f.allocations || []).filter((a) => a.key); // drop the empty placeholder
      if (rows.some((a) => a.key === key)) return f;
      return { ...f, allocations: [...rows, { key, amount: "" }] };
    });
    clearErr("allocations");
  };
  const setSingleKey = (key) => {
    setForm((f) => ({ ...f, allocations: [{ key, amount: (f.allocations?.[0]?.amount) ?? "" }] }));
    clearErr("allocations");
  };
  const removeAllocation = (key) => {
    setForm((f) => {
      const rows = (f.allocations || []).filter((a) => a.key !== key);
      return { ...f, allocations: rows.length ? rows : [{ key: "", amount: "" }] };
    });
  };
  const setAllocAmount = (key, amount) => {
    setForm((f) => ({
      ...f,
      allocations: (f.allocations || []).map((a) => (a.key === key ? { ...a, amount } : a)),
    }));
    clearErr("amount");
  };

  const onSpendType = (v) => {
    setForm((f) => ({
      ...f,
      spend_type: v,
      corporate_entity: "",
      vertical: isManager ? f.vertical : "",
      allocations: [{ key: "", amount: "" }],
    }));
    clearErr("spend_type"); clearErr("vertical"); clearErr("allocations");
  };

  const onVertical = (v) => { set("vertical", v); clearErr("vertical"); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) { toast.error("Only PDF, PNG, JPG, JPEG or WEBP files are allowed"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large — maximum 15 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.uploadFile(fd);
      setForm((f) => ({ ...f, invoice_file_id: res.file_id, invoice_file_name: res.original_filename }));
      clearErr("invoice_file_id");
      toast.success("Invoice attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = () => setForm((f) => ({ ...f, invoice_file_id: "", invoice_file_name: "" }));

  const validate = () => {
    const e = {};
    if (!(form.period || "").toString().trim()) e.period = "Billing period is required";
    if (!form.payment_request) e.payment_request = "Select a payment request";
    if (!(form.duration_months || []).length) e.duration_months = "Select at least one month";
    if (!form.spend_month) e.spend_month = "Select the invoice month";
    if (!form.spend_type) e.spend_type = "Select a spend type";

    const rows = (form.allocations || []).filter((a) => a.key);
    if (!rows.length) {
      e.allocations = isCorporate ? "Add at least one corporate entity" : "Add at least one project";
    } else if (rows.some((a) => !(parseFloat(a.amount) > 0))) {
      e.amount = "Enter a positive amount for every " + (isCorporate ? "entity" : "project");
    }

    if (!form.vertical) e.vertical = "Select a vertical";
    if (canSeePayment && !form.payment_status) e.payment_status = "Select a payment status";
    if (!form.vis_perf) e.vis_perf = "Select visibility / performance";
    if (!form.collateral_type) e.collateral_type = "Select a collateral type";
    if (!form.platform) e.platform = "Select a platform";
    if (!form.vendor) e.vendor = "Select a vendor";
    if (!form.billing_business) e.billing_business = "Select a billing business";
    if (!form.invoice_file_id) e.invoice_file_id = "Upload the invoice file";
    setErrors(e);
    if (Object.keys(e).length) toast.error("Please complete all required fields");
    return Object.keys(e).length === 0;
  };

  const basePayload = () => ({
    spend_month: form.spend_month, invoice_month: form.invoice_month || form.spend_month,
    spend_type: form.spend_type || "Project", spend_scope: form.spend_type || "Project",
    payment_status: canSeePayment ? (form.payment_status || "Unpaid") : (form.payment_status || "Unpaid"),
    vertical: form.vertical,
    sub_line_item: "", sub_vertical: "",
    vis_perf: form.vis_perf, collateral_type: form.collateral_type, platform: form.platform || "Other / NA",
    vendor: form.vendor, billing_business: form.billing_business,
    // marketing verticals are always Marketing; otherwise use the chosen value (blank -> Marketing on backend)
    cost_type: costLocked ? "Marketing" : (form.cost_type || "").trim(),
    cost_bucket: costLocked ? "Marketing" : (form.cost_type || "").trim(),
    activity: (form.activity || "").trim(), outcome: (form.outcome || "").trim(),
    performance: "No",
    invoice_file_id: form.invoice_file_id, invoice_file_name: form.invoice_file_name,
    campaign_id: form.campaign_id, locked: form.locked,
    proforma_text: (form.proforma_text || "").trim(), period: (form.period || "").toString().trim(),
    duration_months: form.duration_months || [], payment_request: form.payment_request || "Complete",
    remark: (form.remark || "").trim(),
  });

  const save = async (addAnother) => {
    if (!validate()) return;
    setSaving(true);
    const base = basePayload();
    const rows = (form.allocations || []).filter((a) => a.key);
    try {
      if (isEdit) {
        const a = rows[0];
        const payload = {
          ...base,
          corporate_entity: isCorporate ? a.key : "",
          project: a.key,
          amount: parseFloat(a.amount),
        };
        await api.updateSpend(editing.id, payload);
        toast.success("Invoice updated");
      } else {
        let done = 0;
        for (const a of rows) {
          setSaveMsg(rows.length > 1 ? `Saving ${done + 1}/${rows.length}…` : "");
          const payload = {
            ...base,
            corporate_entity: isCorporate ? a.key : "",
            project: a.key,
            amount: parseFloat(a.amount),
          };
          // eslint-disable-next-line no-await-in-loop
          await api.createSpend(payload);
          done += 1;
        }
        toast.success(rows.length > 1
          ? `${rows.length} proforma invoices created (one per ${isCorporate ? "entity" : "project"})`
          : "Proforma invoice created");
      }
      onSaved && (await onSaved());
      if (addAnother && !isEdit) {
        setForm((f) => ({
          ...EMPTY, spend_month: f.spend_month, spend_type: f.spend_type,
          vertical: f.vertical, vendor: f.vendor, billing_business: f.billing_business,
          platform: f.platform, collateral_type: f.collateral_type, vis_perf: f.vis_perf,
          period: f.period, payment_request: f.payment_request, duration_months: f.duration_months,
          payment_status: f.payment_status,
          allocations: [{ key: "", amount: "" }],
        }));
      } else onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save invoice");
    } finally { setSaving(false); setSaveMsg(""); }
  };

  const Err = ({ k }) => (errors[k] ? <p className="mt-1 text-xs text-[hsl(var(--danger))]" data-testid={`bill-err-${k}`}>{errors[k]}</p> : null);
  const ReqStar = () => <span className="text-[hsl(var(--danger))]"> *</span>;

  const SectionTitle = ({ icon: Icon, children }) => (
    <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" /> {children}
    </p>
  );

  const rows = (form.allocations || []).filter((a) => a.key);
  const multiCount = rows.length;

  if (!lists) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-2xl" data-testid="spend-drawer">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle className="font-heading">{isEdit ? "Edit Proforma Invoice" : "New Proforma Invoice"}</SheetTitle>
          <SheetDescription>{isEdit ? "Update this invoice's billing details and classification." : "Log a proforma invoice — attach the invoice file and it goes straight to Finance."}</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 px-6 py-5">
          {/* 1) Billing & request */}
          <div className="rounded-xl border p-4" data-testid="proforma-billing-section">
            <SectionTitle icon={ReceiptText}>Billing &amp; Request</SectionTitle>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Billing Period<ReqStar /></Label>
                  <Input value={form.period} onChange={(e) => { set("period", e.target.value); clearErr("period"); }}
                    placeholder="e.g. Apr–Jun 2026" className={cn("mt-1", errors.period && "border-[hsl(var(--danger))]")} data-testid="proforma-billing-period" />
                  <Err k="period" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Payment Request<ReqStar /></Label>
                  <RadioGroup value={form.payment_request} onValueChange={(v) => { set("payment_request", v); clearErr("payment_request"); }}
                    className="mt-2 flex flex-wrap gap-x-4 gap-y-2" data-testid="proforma-payment-request-type">
                    {PAYMENT_REQUEST_TYPES.map((t) => (
                      <label key={t} htmlFor={`pr-${t}`} className="flex cursor-pointer items-center gap-1.5 text-sm">
                        <RadioGroupItem value={t} id={`pr-${t}`} data-testid={`proforma-payment-request-${t.toLowerCase()}`} />
                        {t}
                      </label>
                    ))}
                  </RadioGroup>
                  <Err k="payment_request" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Duration — months covered<ReqStar /></Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="proforma-duration-months">
                  {lists.months.map((m) => {
                    const active = (form.duration_months || []).includes(m);
                    return (
                      <button key={m} type="button" data-testid={`proforma-duration-${m}`}
                        onClick={() => { set("duration_months", active ? form.duration_months.filter((x) => x !== m) : [...(form.duration_months || []), m]); clearErr("duration_months"); }}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors duration-150",
                          active
                            ? "border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]"
                            : "border-border text-muted-foreground hover:bg-secondary"
                        )}>
                        {m}
                      </button>
                    );
                  })}
                </div>
                <Err k="duration_months" />
              </div>
              <div className="sm:max-w-[50%]">
                <Label className="text-xs font-medium text-muted-foreground">Invoice Month<ReqStar /></Label>
                <Select value={form.spend_month || ""} onValueChange={(v) => { set("spend_month", v); clearErr("spend_month"); }}>
                  <SelectTrigger className={cn("mt-1", errors.spend_month && "border-[hsl(var(--danger))]")} data-testid="bill-spend_month-select">
                    <SelectValue placeholder="Select invoice month" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {lists.months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Err k="spend_month" />
              </div>
            </div>
          </div>

          {/* 2) Classification */}
          <div className="rounded-xl border p-4" data-testid="proforma-classification-section">
            <SectionTitle icon={Layers}>Classification</SectionTitle>
            <div className="mb-4">
              <Label className="text-xs font-medium text-muted-foreground">Spend Type<ReqStar /></Label>
              <RadioGroup value={form.spend_type} onValueChange={onSpendType}
                className="mt-2 flex flex-wrap gap-x-5 gap-y-2" data-testid="bill-spend-type-radio">
                {SPEND_TYPES.map((t) => (
                  <label key={t} htmlFor={`st-${t}`} className="flex cursor-pointer items-center gap-1.5 text-sm">
                    <RadioGroupItem value={t} id={`st-${t}`} data-testid={`bill-spend-type-${t.toLowerCase()}`} />
                    {t}
                  </label>
                ))}
              </RadioGroup>
              <p className="mt-1 text-[10px] text-muted-foreground">
                {isCorporate
                  ? "Corporate spends are attributed to an entity (Sumadhura / Signet / Tech), not a project."
                  : "Project spends are tied to a specific project's performance."}
              </p>
              <Err k="spend_type" />
            </div>

            {/* Project(s) / Entities — with per-item amount allocation */}
            <div className="mb-4">
              <Label className="text-xs font-medium text-muted-foreground">
                {isCorporate ? "Corporate Entity(ies)" : "Project(s)"}<ReqStar />
              </Label>
              {isEdit ? (
                <Select value={rows[0]?.key || ""} onValueChange={setSingleKey}>
                  <SelectTrigger className={cn("mt-1", errors.allocations && "border-[hsl(var(--danger))]")} data-testid="bill-project-select">
                    <SelectValue placeholder={isCorporate ? "Select entity" : "Select project"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {isCorporate
                      ? entityOpts.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)
                      : (lists.projects || []).map((p) => <SelectItem key={p.name} value={p.name}>{p.short || p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <Select value="" onValueChange={addAllocation}>
                    <SelectTrigger className={cn("mt-1", errors.allocations && "border-[hsl(var(--danger))]")} data-testid="bill-project-add-select">
                      <SelectValue placeholder={isCorporate ? "Add an entity…" : "Add a project…"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {addableOptions.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">All added</div>}
                      {isCorporate
                        ? addableOptions.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)
                        : addableOptions.map((p) => <SelectItem key={p.name} value={p.name}>{p.short || p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {multiCount > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="bill-project-chips">
                      {rows.map((a) => (
                        <span key={a.key} data-testid={`bill-project-chip-${a.key}`}
                          className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--primary)/0.35)] bg-[hsl(var(--primary)/0.10)] px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--primary))]">
                          {allocKeyLabel(a.key)}
                          <button type="button" onClick={() => removeAllocation(a.key)} aria-label={`Remove ${a.key}`}
                            className="rounded-full p-0.5 hover:bg-[hsl(var(--primary)/0.2)]" data-testid={`bill-project-remove-${a.key}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {isCorporate ? "Add one or more entities" : "Add one or more projects"} — you'll set the amount for each below.
                  </p>
                </>
              )}
              <Err k="allocations" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Vertical */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Vertical<ReqStar /></Label>
                {managerLockVertical ? (
                  <div className="mt-1 flex h-10 items-center gap-2 rounded-md border bg-secondary/50 px-3 text-sm" data-testid="bill-vertical-locked">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{verticalLabels[lockedVertical] || lockedVertical}</span>
                  </div>
                ) : (
                  <Select value={form.vertical || ""} onValueChange={onVertical}>
                    <SelectTrigger className={cn("mt-1", errors.vertical && "border-[hsl(var(--danger))]")} data-testid="bill-vertical-select">
                      <SelectValue placeholder={isCorporate ? "Select corporate vertical" : "Select project vertical"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {verticalOpts.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No verticals available</div>}
                      {verticalOpts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Err k="vertical" />
              </div>

              {/* Platform */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Platform<ReqStar /></Label>
                <CreatableCombobox className="mt-1" value={form.platform} error={!!errors.platform}
                  options={platformOptions} testId="bill-platform-combobox"
                  placeholder="Select or type a platform" searchPlaceholder="Search or add platform…"
                  onValueChange={(v) => { set("platform", v); clearErr("platform"); }}
                  onCreate={(v) => createValue("platforms", v)} />
                <Err k="platform" />
              </div>

              {/* Collateral Type */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Collateral Type<ReqStar /></Label>
                <CreatableCombobox className="mt-1" value={form.collateral_type} error={!!errors.collateral_type}
                  options={collateralOptions} testId="bill-collateral_type-combobox"
                  placeholder="Select or type a collateral type" searchPlaceholder="Search or add collateral type…"
                  onValueChange={(v) => { set("collateral_type", v); clearErr("collateral_type"); }}
                  onCreate={(v) => createValue("collateral_types", v)} />
                <Err k="collateral_type" />
              </div>

              {/* Visibility / Performance */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Visibility / Performance<ReqStar /></Label>
                <CreatableCombobox className="mt-1" value={form.vis_perf} error={!!errors.vis_perf}
                  options={visPerfOptions} testId="bill-vis_perf-combobox"
                  placeholder="Select or type visibility / performance" searchPlaceholder="Search or add visibility / performance…"
                  onValueChange={(v) => { set("vis_perf", v); clearErr("vis_perf"); }}
                  onCreate={(v) => createValue("vis_perf", v)} />
                <Err k="vis_perf" />
              </div>

              {/* Vendor Name */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Vendor Name<ReqStar /></Label>
                <CreatableCombobox className="mt-1" value={form.vendor} error={!!errors.vendor}
                  options={vendorOptions} testId="bill-vendor-combobox"
                  placeholder="Select or type a vendor" searchPlaceholder="Search or add vendor…"
                  onValueChange={(v) => { set("vendor", v); clearErr("vendor"); }}
                  onCreate={(v) => createValue("vendors", v)} />
                <Err k="vendor" />
              </div>

              {/* Billing Business Name */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Billing Business Name<ReqStar /></Label>
                <CreatableCombobox className="mt-1" value={form.billing_business} error={!!errors.billing_business}
                  options={billingOptions} testId="bill-billing_business-combobox"
                  placeholder="Select or type a billing business" searchPlaceholder="Search or add billing business…"
                  onValueChange={(v) => { set("billing_business", v); clearErr("billing_business"); }}
                  onCreate={(v) => createValue("billing_businesses", v)} />
                <Err k="billing_business" />
              </div>

              {/* Logged By (auto) */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Logged By (auto)</Label>
                <div className="mt-1 flex h-10 items-center gap-2 rounded-md border bg-secondary/50 px-3 text-sm text-muted-foreground" data-testid="bill-logged-by-auto">
                  <User className="h-3.5 w-3.5" /> {user?.name || user?.email || "Current user"}
                </div>
              </div>
            </div>
          </div>

          {/* 3) Spend categorization (reporting taxonomy) */}
          <div className="rounded-xl border p-4" data-testid="proforma-categorization-section">
            <SectionTitle icon={Tags}>Spend Categorization</SectionTitle>
            <p className="-mt-1 mb-3 text-[11px] text-muted-foreground">Used for investment-mix &amp; performance reporting.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Cost Type</Label>
                {costLocked ? (
                  <div className="mt-1 flex h-10 items-center gap-2 rounded-md border bg-secondary/50 px-3 text-sm" data-testid="bill-cost-type-locked">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">Marketing</span>
                  </div>
                ) : (
                  <CreatableCombobox className="mt-1" value={form.cost_type}
                    options={costTypeOptions} testId="bill-cost_type-combobox"
                    placeholder="Marketing / Operational / Pre-Sales" searchPlaceholder="Search or add cost type…"
                    onValueChange={(v) => set("cost_type", v)}
                    onCreate={(v) => createValue("cost_types", v)} />
                )}
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {costLocked ? "Marketing verticals are always classified as Marketing." : "Leave blank to default to Marketing."}
                </p>
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Activity</Label>
                <CreatableCombobox className="mt-1" value={form.activity}
                  options={activityOptions} testId="bill-activity-combobox"
                  placeholder="Select or type an activity" searchPlaceholder="Search or add activity…"
                  onValueChange={(v) => set("activity", v)}
                  onCreate={(v) => createValue("activities", v)} />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Outcome</Label>
                <CreatableCombobox className="mt-1" value={form.outcome}
                  options={outcomeOptions} testId="bill-outcome-combobox"
                  placeholder="Select or type an outcome" searchPlaceholder="Search or add outcome…"
                  onValueChange={(v) => set("outcome", v)}
                  onCreate={(v) => createValue("outcomes", v)} />
              </div>
            </div>
          </div>

          {/* 4) Invoice file + amount allocation */}
          <div className="rounded-xl border p-4" data-testid="proforma-invoice-section">
            <SectionTitle icon={ReceiptText}>Invoice &amp; Amount</SectionTitle>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Upload Invoice<ReqStar /></Label>
                {form.invoice_file_id ? (
                  <div className="mt-1.5 flex items-center justify-between gap-3 rounded-lg border border-[hsl(var(--success)/0.4)] bg-[hsl(var(--success)/0.08)] px-3 py-2.5" data-testid="bill-invoice-file-attached">
                    <span className="flex min-w-0 items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--success))]" />
                      <span className="truncate font-medium">{form.invoice_file_name || "Invoice attached"}</span>
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      <a href={api.fileUrl(form.invoice_file_id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.1)]" data-testid="bill-invoice-view-link">
                        <Eye className="h-3.5 w-3.5" /> View
                      </a>
                      <button type="button" onClick={removeFile} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary" data-testid="bill-invoice-remove">
                        <X className="h-3.5 w-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <label htmlFor="invoice-file-input"
                    className={cn(
                      "mt-1.5 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors duration-150 hover:bg-secondary/60",
                      errors.invoice_file_id ? "border-[hsl(var(--danger))]" : "border-border"
                    )}
                    data-testid="bill-invoice-dropzone">
                    {uploading ? <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
                      : <UploadCloud className="h-6 w-6 text-muted-foreground" />}
                    <span className="text-sm font-medium">{uploading ? "Uploading…" : "Click to upload invoice"}</span>
                    <span className="text-[11px] text-muted-foreground">PDF, PNG, JPG or WEBP · max 15 MB</span>
                    <input id="invoice-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onFile} disabled={uploading} data-testid="bill-invoice-file-input" />
                  </label>
                )}
                <Err k="invoice_file_id" />
              </div>

              {/* Amount allocation — one input per selected project/entity */}
              <div className="rounded-xl border bg-secondary/40 p-4">
                <Label className="text-xs font-medium text-muted-foreground">
                  Amount incl. GST (₹)<ReqStar />
                  {multiCount > 1 && <span className="ml-1 font-normal text-[10px] text-muted-foreground">— per {isCorporate ? "entity" : "project"}</span>}
                </Label>
                {multiCount === 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="bill-amount-empty">Add a {isCorporate ? "entity" : "project"} above to enter an amount.</p>
                ) : (
                  <div className="mt-2 space-y-2" data-testid="bill-amount-allocations">
                    {rows.map((a, idx) => (
                      <div key={a.key} className="flex items-center gap-3">
                        {multiCount > 1 && (
                          <span className="w-1/3 truncate text-xs font-medium" title={allocKeyLabel(a.key)}>{allocKeyLabel(a.key)}</span>
                        )}
                        <Input type="number" min="0" value={a.amount}
                          onChange={(e) => setAllocAmount(a.key, e.target.value)}
                          placeholder="0"
                          className={cn("text-right text-lg font-semibold tabular-nums", multiCount > 1 && "flex-1", errors.amount && "border-[hsl(var(--danger))]")}
                          data-testid={`bill-alloc-amount-${idx}`} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between border-t pt-2">
                  <span className="text-[11px] text-muted-foreground">{multiCount > 1 ? "Total" : "Amount"}</span>
                  <span className="text-sm font-semibold tabular-nums" data-testid="bill-total-amount">{total ? formatMoney(total, mode) : "—"}</span>
                </div>
                <Err k="amount" />
              </div>

              {/* Payment Status — Finance Executive + Admin/Leader only */}
              {canSeePayment && (
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Payment Status<ReqStar /></Label>
                  <Select value={form.payment_status || "Unpaid"} onValueChange={(v) => { set("payment_status", v); clearErr("payment_status"); }}>
                    <SelectTrigger className={cn("mt-1 sm:max-w-[50%]", errors.payment_status && "border-[hsl(var(--danger))]")} data-testid="bill-payment-status-select">
                      <SelectValue placeholder="Select payment status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[10px] text-muted-foreground">Finance updates this — or bulk-update later from Data → Payment status.</p>
                  <Err k="payment_status" />
                </div>
              )}
            </div>
          </div>

          {/* Optional details */}
          <div className="rounded-xl border p-4" data-testid="proforma-optional-section">
            <SectionTitle icon={FileText}>Notes (optional)</SectionTitle>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Description / scope of work</Label>
                <Textarea value={form.proforma_text} onChange={(e) => set("proforma_text", e.target.value)}
                  rows={2} placeholder="What is this proforma invoice for?" className="mt-1" data-testid="proforma-text-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Remarks for Finance</Label>
                <Textarea value={form.remark} onChange={(e) => set("remark", e.target.value)}
                  rows={2} placeholder="Note for Finance" className="mt-1" data-testid="proforma-remarks" />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Campaign (optional)</Label>
                <Select value={form.campaign_id || NONE} onValueChange={(v) => set("campaign_id", v === NONE ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="bill-campaign-select"><SelectValue placeholder="No campaign" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={NONE}>No campaign</SelectItem>
                    {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-card px-6 py-4">
          {saveMsg && <span className="mr-auto text-xs text-muted-foreground" data-testid="bill-save-progress">{saveMsg}</span>}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="add-bill-cancel-button">Cancel</Button>
          {!isEdit && <Button variant="outline" disabled={saving || uploading} onClick={() => save(true)} data-testid="add-bill-save-add-another-button">Save &amp; Add Another</Button>}
          <Button disabled={saving || uploading} onClick={() => save(false)} data-testid="add-bill-save-button">{saving ? "Saving…" : isEdit ? "Update Invoice" : (multiCount > 1 ? `Create ${multiCount} Invoices` : "Create Proforma")}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
